import { useEffect, useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../lib/api'
import { PUBLIC_ORIGIN } from '../../lib/config'
import { Spinner } from '../ui/Spinner'
import { BrandLogo } from '../ui/BrandLogo'

const PUBLIC_HOST = PUBLIC_ORIGIN.replace(/^https?:\/\//, '')

/** Shown in the editor when the signed-in user hasn't claimed a username yet. */
export function ClaimPrompt({ onClaim }: { onClaim: (username: string) => Promise<void> }) {
  const [username, setUsername] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const normalized = username.trim().toLowerCase()

  const [debounced, setDebounced] = useState(normalized)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(normalized), 300)
    return () => clearTimeout(id)
  }, [normalized])

  const availability = useQuery(
    api.pages.checkUsernameAvailable,
    debounced.length >= 3 ? { username: debounced } : 'skip',
  )
  const ok = debounced.length >= 3 && availability?.available === true

  async function submit() {
    if (!ok) return
    setPending(true)
    setError(null)
    try {
      await onClaim(debounced)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not claim that name')
      setPending(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-8">
        <div className="mb-7 text-white"><BrandLogo /></div>
        <h1 className="text-2xl font-extrabold text-white">Claim your page</h1>
        <p className="mt-2 text-sm text-zinc-400">Pick a username for your MakerStack page.</p>
        <div className="mt-6 flex items-center rounded-xl border border-zinc-700 bg-zinc-950 px-3 focus-within:border-fuchsia-500">
          <span className="text-sm text-zinc-500">{PUBLIC_HOST}/</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="yourname"
            autoFocus
            autoCapitalize="none"
            spellCheck={false}
            className="w-full bg-transparent py-3 pl-1 text-sm text-white outline-none placeholder:text-zinc-600"
          />
        </div>
        {debounced.length >= 3 && availability && !availability.available && (
          <p className="mt-2 text-sm text-rose-400">{availability.reason}</p>
        )}
        {error && <p className="mt-2 text-sm text-rose-400">{error}</p>}
        <button
          onClick={submit}
          disabled={!ok || pending}
          className="mt-5 flex w-full items-center justify-center rounded-xl bg-fuchsia-500 py-3 text-sm font-bold text-white hover:bg-fuchsia-400 disabled:opacity-40"
        >
          {pending ? <Spinner className="h-4 w-4" /> : 'Continue'}
        </button>
      </div>
    </div>
  )
}
